import { type ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/common/components/ui/table";
import { Skeleton } from "@/common/components/ui/skeleton";
import { Activity } from "lucide-react";
import {
  GetLedgerAccountLastEntriesDocument,
  type GetLedgerAccountLastEntriesQuery,
  type GetLedgerAccountLastEntriesQueryVariables,
} from "@/graphql/definitions";
import { formatDateISO } from "@/common/lib/format/format-date-iso";
import { Link, useParams } from "@tanstack/react-router";
import { useLedgerSearchParams } from "@/common/hooks/use-ledger-search-params";
import { useTranslations } from "@/common/hooks/use-translations";
import { useErrorMessage } from "@/common/lib/errors/error-message";
import { useQueryRetry } from "@/common/hooks/use-query-retry";
import { formatDecimalCell } from "@/common/lib/format/inventory-cell";
import { StatisticsErrorState, StatisticsSection } from "./statistics-section";

/**
 * Format a balance map for display.
 *
 * These are unit quantities, not derived ratios, so they go through the shared
 * lossless decimal formatting Holdings and BQL already use. Running them
 * through `Number` and the locale number formatter silently rounded 4.00995
 * ETH to 4.01 — a quantity the reader cannot reconcile against Holdings.
 */
function formatBalance(balance: Record<string, unknown> | null): ReactNode {
  if (!balance) return "N/A";

  const entries = Object.entries(balance);
  if (entries.length === 0) return "0";

  return entries.map(([currency, amount]) => (
    <span
      key={currency}
      className="block"
    >{`${formatDecimalCell(amount)} ${currency}`}</span>
  ));
}

/**
 * Loading state component for account last entries table
 */
function AccountLastEntriesLoadingState() {
  const { t } = useTranslations();
  return (
    <StatisticsSection
      icon={Activity}
      title={t("page.statistics.accountLastEntries")}
      description={t("common.loadingData")}
    >
      <TableHeader>
        <TableRow className="bg-muted/50">
          <TableHead className="whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2 text-muted-foreground text-xs sm:text-sm">
            {t("component.searchControls.account")}
          </TableHead>
          <TableHead className="whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2 text-muted-foreground text-xs sm:text-sm">
            {t("page.statistics.lastEntryDate")}
          </TableHead>
          <TableHead className="text-right whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2 text-muted-foreground text-xs sm:text-sm">
            {t("journal.balance")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 8 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton className="h-3 sm:h-4 w-32 sm:w-48" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-3 sm:h-4 w-20 sm:w-24 ml-auto" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </StatisticsSection>
  );
}

/**
 * Account last entries table component
 */
function AccountLastEntriesTable({
  data,
}: {
  data: GetLedgerAccountLastEntriesQuery;
}) {
  const { t } = useTranslations();
  const { ledgerOwner, ledgerName } = useParams({
    from: "/ledger/$ledgerOwner/$ledgerName/statistics",
  });
  const entries = data.getLedgerAccountLastEntries;

  return (
    <StatisticsSection
      icon={Activity}
      title={t("page.statistics.accountLastEntries")}
      description={t("page.statistics.accountLastEntriesDescription", {
        count: entries.length,
      })}
    >
      <TableHeader>
        <TableRow className="bg-muted/50">
          <TableHead className="whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2 text-muted-foreground text-xs sm:text-sm">
            {t("component.searchControls.account")}
          </TableHead>
          <TableHead className="whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2 text-muted-foreground text-xs sm:text-sm">
            {t("page.statistics.lastEntryDate")}
          </TableHead>
          <TableHead className="text-right whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2 text-muted-foreground text-xs sm:text-sm">
            {t("journal.balance")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => (
          <TableRow key={entry.account}>
            <TableCell className="font-medium font-mono text-xs sm:text-sm break-all max-w-[200px] sm:max-w-none px-2 sm:px-3 py-1.5 sm:py-2">
              <Link
                to="/ledger/$ledgerOwner/$ledgerName/account/$accountName"
                params={{
                  ledgerOwner,
                  ledgerName,
                  accountName: entry.account,
                }}
                className="text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              >
                {entry.account}
              </Link>
            </TableCell>
            <TableCell className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2">
              {formatDateISO(entry.date) || "N/A"}
            </TableCell>
            <TableCell className="text-right font-mono text-xs sm:text-sm whitespace-nowrap px-2 sm:px-3 py-1.5 sm:py-2">
              {formatBalance(entry.balance)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </StatisticsSection>
  );
}

/**
 * Account Last Entries Component
 * Self-contained component that fetches and displays account last entry information
 */
export function AccountLastEntries({ ledgerId }: { ledgerId: string }) {
  const { t } = useTranslations();
  const ledgerFilters = useLedgerSearchParams();
  const formatError = useErrorMessage();

  const { data, loading, error, refetch } = useQuery<
    GetLedgerAccountLastEntriesQuery,
    GetLedgerAccountLastEntriesQueryVariables
  >(GetLedgerAccountLastEntriesDocument, {
    variables: {
      ledgerId: ledgerId,
      time: ledgerFilters.searchParams.time,
      filter: ledgerFilters.searchParams.filter,
      account: ledgerFilters.searchParams.account,
    },
    skip: !ledgerId,
  });

  const { failure, retrying, retry } = useQueryRetry(error, refetch);

  if (failure) {
    return (
      <StatisticsErrorState
        description={t("page.statistics.failedToLoadAccountEntries")}
        message={formatError(failure)}
        onRetry={retry}
        retrying={retrying}
      />
    );
  }
  if (loading) {
    return <AccountLastEntriesLoadingState />;
  }

  if (!data) {
    return null;
  }

  return <AccountLastEntriesTable data={data} />;
}
